import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableHeader, TableBody, TableCell, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Search,
  Plus,
  X,
  Edit2,
  Trash2,
  Save,
  User,
  Mail,
} from 'lucide-react'

// Type definition for Address

type Address = { 
  name: string
  email: string
  id?: string
  initials?: string
}

// Updated AddressBook component with compact rows, side-by-side actions, and bottom close

export const AddressBook = ({
  addresses,
  onUpdate,
  onClose,
}: {
  addresses: Address[]
  onUpdate: (addresses: Address[]) => void
  onClose: () => void
}) => {
  const [local, setLocal] = useState<Address[]>(addresses)
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [form, setForm] = useState<Address>({ name: '', email: '' })
  const [adding, setAdding] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const addressesWithInitials = addresses.map(addr => ({
      ...addr,
      initials: getInitials(addr.name),
      id: addr.id || generateId()
    }))
    setLocal(addressesWithInitials)
  }, [addresses])

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  const generateId = (): string => {
    return Math.random().toString(36).substr(2, 9)
  }

  const filteredContacts = local.filter(contact => 
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const startEdit = (idx: number) => {
    setEditingIdx(idx)
    setForm(local[idx])
  }
  const cancelEdit = () => {
    setEditingIdx(null)
    setForm({ name: '', email: '' })
  }
  const saveEdit = () => {
    const updated = local.map((a, i) => (i === editingIdx ? {
      ...form,
      initials: getInitials(form.name),
      id: a.id
    } : a))
    setLocal(updated)
    onUpdate(updated)
    cancelEdit()
  }
  const startAdd = () => {
    setForm({ name: '', email: '' })
    setAdding(true)
    setEditingIdx(null)
  }
  const cancelAdd = () => {
    setAdding(false)
    setForm({ name: '', email: '' })
  }
  const saveAdd = () => {
    const newContact = {
      ...form,
      initials: getInitials(form.name),
      id: generateId()
    }
    const updated = [...local, newContact]
    setLocal(updated)
    onUpdate(updated)
    cancelAdd()
  }
  const deleteItem = (idx: number) => {
    const updated = local.filter((_, i) => i !== idx)
    setLocal(updated)
    onUpdate(updated)
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] w-[80vw] h-[85vh] p-0 flex flex-col gap-0 overflow-hidden bg-white sm:rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-gray-600" />
            <DialogTitle className="text-lg font-semibold text-gray-900">Address Book</DialogTitle>
            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
              {local.length} contacts
            </span>
          </div>
          <div className="flex items-center gap-2 mr-8">
            <Button
              onClick={startAdd}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 text-sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-80 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {searchQuery && (
              <span className="text-sm text-gray-500">
                {filteredContacts.length} of {local.length} contacts
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {adding && 'Adding new contact...'}
            {editingIdx !== null && 'Editing contact...'}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableCell className="font-semibold text-gray-700 py-3 px-4 w-[40%]">Name</TableCell>
                  <TableCell className="font-semibold text-gray-700 py-3 px-4 w-[45%]">Email Address</TableCell>
                  <TableCell className="font-semibold text-gray-700 py-3 px-4 w-[15%] text-center">Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adding && (
                  <TableRow className="bg-blue-50 border-l-4 border-blue-400">
                    <TableCell className="py-3 px-4">
                      <Input
                        placeholder="Full name"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className="border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Input
                        type="email"
                        placeholder="email@company.com"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          onClick={saveAdd}
                          disabled={!form.name.trim() || !form.email.trim()}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                        <Button 
                          variant="ghost" 
                          onClick={cancelAdd}
                          size="sm"
                          className="text-gray-600 hover:text-gray-800 px-3 py-1 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
          
                {filteredContacts.length === 0 && !adding ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-12 text-center">
                      <div className="flex flex-col items-center">
                        <Mail className="h-12 w-12 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {searchQuery ? 'No matches found' : 'No contacts'}
                        </h3>
                        <p className="text-gray-500 mb-4">
                          {searchQuery 
                            ? `No contacts match "${searchQuery}"` 
                            : 'Your address book is empty'}
                        </p>
                        {!searchQuery && (
                          <Button 
                            onClick={startAdd} 
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Contact
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContacts.map((addr, idx) => (
                    <TableRow 
                      key={addr.id || idx} 
                      className={`hover:bg-gray-50 ${
                        editingIdx === idx ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''
                      }`}
                    >
                      <TableCell className="py-3 px-4">
                        {editingIdx === idx ? (
                          <Input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            className="border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {addr.initials || getInitials(addr.name)}
                            </div>
                            <span className="font-medium text-gray-900">{addr.name}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        {editingIdx === idx ? (
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            className="border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-gray-700">{addr.email}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          {editingIdx === idx ? (
                            <>
                              <Button 
                                onClick={saveEdit}
                                disabled={!form.name.trim() || !form.email.trim()}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs"
                              >
                                <Save className="h-3 w-3 mr-1" />
                                Save
                              </Button>
                              <Button 
                                variant="ghost" 
                                onClick={cancelEdit}
                                size="sm"
                                className="text-gray-600 hover:text-gray-800 px-3 py-1 text-xs"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(idx)}
                                className="text-gray-400 hover:text-blue-600 p-1"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteItem(idx)}
                                className="text-gray-400 hover:text-red-600 p-1"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
